import JSZip from 'jszip';
import MicroModal from 'micromodal';

import { exportProcessCellCallback, getExportFileName } from './helpers';

// Define buttons for various actions
const restoreButton = document.querySelector('[data-table-action=restore]');
const csvButton = document.querySelector('[data-table-action=csv]');
const filterButton = document.querySelector('[data-table-action=filter]');
const excelButton = document.querySelector('[data-table-action=excel]');
const zipButton = document.querySelector('[data-table-action=zip]');
const printButton = document.querySelector('[data-table-action=print]');
const notesButton = document.querySelector('[data-table-action=notes]');
const hideButton = document.querySelector('[data-table-action=hide]');

// Variables for storing references
let DataPageInstance; // Reference to the DataPages instance
let gridAPI; // Reference to the ag-Grid API

// Variables for storing the Grid state on initialization. They are used for the restore button.
let colState;
let floatFilterState;
let advancedFilterState;

/**
 * Initializes the action buttons and their click event handlers.
 *
 * @param {DataPages} classRef - Reference to the DataPages class instance.
 */
export default function initializeActionButtons(classRef) {
    DataPageInstance = classRef;
    gridAPI = DataPageInstance.getGridApi();

    // Restore Button
    if (restoreButton) {
        colState = gridAPI.getColumnState();
        floatFilterState = gridAPI.getFilterModel();
        advancedFilterState = gridAPI.getAdvancedFilterModel();

        restoreButton.addEventListener('click', restoreButtonClick);
    }

    // CSV Export Button
    if (csvButton) {
        csvButton.addEventListener('click', csvButtonClick);
    }

    // Filter Button
    if (filterButton) {
        filterButton.addEventListener('click', filterButtonClick);
    }

    // Excel Export Button
    if (excelButton) {
        excelButton.addEventListener('click', excelButtonClick);
    }

    // ZIP Export Button
    if (zipButton) {
        zipButton.addEventListener('click', zipButtonClick);
    }

    // Print Button
    if (printButton) {
        printButton.addEventListener('click', printButtonClick);
    }

    // Notes Button
    if (notesButton) {
        notesButton.addEventListener('click', notesButtonClick);
    }

    // Hide Button
    if (hideButton) {
        hideButton.addEventListener('click', hideButtonClick);
    }
}

/**
 * Initializes the "Restore" button click event handler.
 * Restores grid state to the previous configuration.
 */
const restoreButtonClick = () => {
    gridAPI.closeToolPanel();
    gridAPI.setFilterModel(floatFilterState);
    gridAPI.applyColumnState({
        state: colState,
        applyOrder: true,
    });
    gridAPI.setAdvancedFilterModel(advancedFilterState);

    closeAdvancedFilterCloseButton();
};

/**
 * Initializes the "CSV Export" button click event handler.
 * Exports data to CSV format.
 */
const csvButtonClick = () => {
    if (DataPageInstance.ServerSideExportInstance) {
        DataPageInstance.ServerSideExportInstance.export('csv');
    } else {
        const fileName = getExportFileName() ?? 'csv';
        gridAPI.exportDataAsCsv({
            fileName: fileName,
            processCellCallback: exportProcessCellCallback,
        });
    }
};

/**
 * Initializes the "Filter" button click event handler.
 * Toggles the filter panel visibility and advanced filter builder.
 */
const filterButtonClick = () => {
    if (!gridAPI.isToolPanelShowing('filters')) {
        gridAPI.openToolPanel('filters');
    } else {
        gridAPI.closeToolPanel();
    }

    const closeBtn = closeAdvancedFilterCloseButton();

    if (!closeBtn) {
        gridAPI.showAdvancedFilterBuilder();
    }
};

/**
 * Initializes the "Excel Export" button click event handler.
 * Exports data to Excel format.
 */
const excelButtonClick = () => {
    if (DataPageInstance.ServerSideExportInstance) {
        DataPageInstance.ServerSideExportInstance.export('excel');
    } else {
        const fileName = getExportFileName() ?? 'excel';
        gridAPI.exportDataAsExcel({
            fileName: fileName,
            processCellCallback: exportProcessCellCallback,
        });
    }
};

/**
 * Initializes the "ZIP Export" button click event handler.
 * Exports data to ZIP format containing CSV and Excel files.
 */
const zipButtonClick = () => {
    let zip = new JSZip();
    let csvData = gridAPI.getDataAsCsv();
    let excelData = gridAPI.getDataAsExcel();

    zip.file('data.csv', csvData);
    zip.file('data.xls', excelData);
    let result = zip.generateAsync({ type: 'blob' });

    result.then((zip) => {
        const zipURL = window.URL.createObjectURL(zip);
        let link = document.createElement('a');
        link.href = zipURL;
        link.download = 'data.zip';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(zipURL);
    });
};

/**
 * Initializes the "Print" button click event handler.
 * Prints the current view.
 */
const printButtonClick = () => {
    window.print();
};

/**
 * Initializes the "Notes" button click event handler.
 * Displays notes and handles scrollbar behavior.
 */
const notesButtonClick = () => {
    MicroModal.show('user-guide-js');
    document.body.classList.add('micromodal-hide-scrollbar');

    const closeElement = document.querySelectorAll('[data-micromodal-close]');

    closeElement.forEach((item) => {
        item.addEventListener('click', function (event) {
            document.body.classList.remove('micromodal-hide-scrollbar');
        });
    });
};

/**
 * Initializes the "Hide" button click event handler.
 * Closes the tool panel.
 */
const hideButtonClick = () => {
    gridAPI.closeToolPanel();
};

/**
 * Closes the advanced filter panel if it is open.
 *
 * @returns {boolean} - True if the panel was closed, false otherwise.
 */
function closeAdvancedFilterCloseButton() {
    const closeBtn = document.querySelector(
        '.ag-panel-title-bar-button .ag-icon-cross',
    );

    if (closeBtn) {
        closeBtn.click();
        return true;
    }

    return false;
}
